import React, { useEffect, useMemo, useState } from "react";
import { formatNumber, formatValue, getFormatDefaults } from "@/lib/chartTheme";
import { formatDateRegional } from "@/context/RegionalContext";
import { buildCondEval, condStyle } from "@/lib/condFormat";
import { t } from "@/lib/i18n";
import { fontCss } from "@/components/bi/FontStyleControls";
import { CondIcon } from "@/components/bi/CondIcon";
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import { ColumnFilterPopover } from "@/components/bi/ColumnFilterPopover";
import { applyColumnFilters, isFilterActive, describeFilter, detectColumnKind } from "@/lib/columnFilter";
import { smartComparator } from "@/lib/filterValues";
import { tableColumnOrder } from "@/lib/tableColumns";

const EMPTY_TOKENS = { dash: "\u2014", zero: "0", blank: "" };

// Blanks always last; numbers numerically; months / quarters / weekdays chronologically; else natural A→Z.
export function sortRows(rows, col, dir, isNum) {
  const present = rows.filter((r) => !(r[col] == null || r[col] === ""));
  const missing = rows.filter((r) => r[col] == null || r[col] === "");
  const cmp = isNum ? (a, b) => Number(a) - Number(b) : smartComparator(present.map((r) => r[col]));
  const mul = dir === "desc" ? -1 : 1;
  present.sort((a, b) => mul * cmp(a[col], b[col]));
  return present.concat(missing);
}

function matchesFilter(raw, q, isNum) {
  q = (q || "").trim();
  if (!q) return true;
  if (isNum) {
    const v = Number(raw);
    let m = q.match(/^(>=|<=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);
    if (m) {
      if (!Number.isFinite(v)) return false;
      const n = Number(m[2]);
      return m[1] === ">" ? v > n : m[1] === "<" ? v < n : m[1] === ">=" ? v >= n : m[1] === "<=" ? v <= n : v === n;
    }
    m = q.match(/^(-?\d+(?:\.\d+)?)\.\.(-?\d+(?:\.\d+)?)$/);
    if (m) return Number.isFinite(v) && v >= Number(m[1]) && v <= Number(m[2]);
  }
  return String(raw ?? "").toLowerCase().includes(q.toLowerCase());
}

// Simple pattern date formatter for column overrides (yyyy/MM/dd/MMM tokens).
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtDatePattern(v, pattern) {
  if (!pattern || v == null || v === "") return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return pattern
    .replace(/yyyy/g, String(d.getFullYear()))
    .replace(/MMM/g, MONTHS[d.getMonth()])
    .replace(/MM/g, pad(d.getMonth() + 1))
    .replace(/dd/g, pad(d.getDate()));
}

// CSS border string from { color, width, style } — only when color or width supplied.
function borderCss(b) {
  if (!b || (!b.color && !b.width)) return null;
  return `${b.width ?? 1}px ${b.style || "solid"} ${b.color || "var(--glass-border)"}`;
}

// Data table (items 24 + 74-77): header/value/per-column formatting, hidden columns,
// per-column filters, wrap, density, striping, banding, hover, multi-layer conditional
// formatting, header sorting & pagination.
export function TableView({ data, config = {}, onConfigChange = null, onRowDrill = null, drillTrigger = "right_click" }) {
  const t9 = config.options?.tableOpts || {};
  const loc = config.options?.locale || undefined;
  const hdr = t9.header || {};
  const val = t9.values || {};
  const [colFilters, setColFilters] = useState({});
  // Item 26: Excel-style header filters. Builder persists them on tableOpts.colFilters; the
  // viewer keeps an in-session copy seeded from the saved config.
  const [localHdrFilters, setLocalHdrFilters] = useState(null);
  const hdrFilters = localHdrFilters ?? (t9.colFilters || {});
  const setHdrFilter = (c, ff) => {
    const next = { ...hdrFilters };
    if (ff) next[c] = ff; else delete next[c];
    setLocalHdrFilters(next);
    if (onConfigChange) onConfigChange({ options: { ...(config.options || {}), tableOpts: { ...t9, colFilters: next } } });
  };
  const [sort, setSort] = useState(t9.defaultSort || null); // {col, dir: "asc"|"desc"}
  const savedSortKey = t9.defaultSort ? `${t9.defaultSort.col}:${t9.defaultSort.dir}` : "";
  useEffect(() => { setSort(t9.defaultSort || null); }, [savedSortKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [page, setPage] = useState(0);
  const uid = useMemo(() => `tbl-${Math.random().toString(36).slice(2, 8)}`, []);
  const ev = buildCondEval(config.options?.condRules, data.rows);
  const measMeta = {}; (data.meta?.measures || []).forEach((m) => { measMeta[m.key] = m; });
  const dimMeta = {}; (data.meta?.dimensions || []).forEach((d) => { dimMeta[d.key] = d; });
  const emptyTok = EMPTY_TOKENS[config.options?.emptyAs || "dash"];
  const hidden = new Set(t9.hidden || []);
  // Item 24: render the author's column order (one entry per column instance); legacy widgets
  // without `table_columns` keep the raw query order.
  const ordered = tableColumnOrder(config, data.columns || []);
  const columns = (ordered || data.columns || []).filter((c) => !hidden.has(c));
  const isNumCol = (c) => !!measMeta[c] || (data.rows || []).some((r) => typeof r[c] === "number");
  const filtersKey = JSON.stringify([colFilters, hdrFilters]);
  const sortKey = sort ? `${sort.col}:${sort.dir}` : "";
  const rows = useMemo(() => {
    const active = Object.entries(colFilters).filter(([c, q]) => q && q.trim() && !hidden.has(c));
    let out = active.length
      ? (data.rows || []).filter((r) => active.every(([c, q]) => matchesFilter(r[c], q, isNumCol(c))))
      : (data.rows || []);
    out = applyColumnFilters(out, Object.fromEntries(Object.entries(hdrFilters).filter(([c]) => !hidden.has(c))));
    if (sort) out = sortRows([...out], sort.col, sort.dir, isNumCol(sort.col));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.rows, filtersKey, sortKey]);

  const pageSize = Number(t9.pageSize) || 0;
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(rows.length / pageSize)) : 1;
  useEffect(() => { setPage(0); }, [filtersKey, sortKey, data.rows, pageSize]);
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = pageSize > 0 ? rows.slice(safePage * pageSize, (safePage + 1) * pageSize) : rows;

  const cycleSort = (c) => {
    const s = sort;
    const next = !s || s.col !== c ? { col: c, dir: "asc" } : s.dir === "asc" ? { col: c, dir: "desc" } : null;
    setSort(next);
    // Builder: persist as the widget's default sort so preview / published render identically.
    if (onConfigChange) onConfigChange({ options: { ...(config.options || {}), tableOpts: { ...t9, defaultSort: next || undefined } } });
  };
  const sortIcon = (c) => {
    if (sort?.col === c) return sort.dir === "asc"
      ? <ArrowUp className="w-3 h-3 text-[color:var(--brand-ocean)] shrink-0" />
      : <ArrowDown className="w-3 h-3 text-[color:var(--brand-ocean)] shrink-0" />;
    return <ArrowUpDown className="w-3 h-3 opacity-0 group-hover/th:opacity-50 shrink-0" />;
  };

  const st = (c) => t9.styles?.[c] || {};
  const { dateFormat: regionalDateFmt, timezone: regionalTz } = getFormatDefaults();
  const labelFor = (c) => measMeta[c]?.label || dimMeta[c]?.label || c.replace(/_/g, " ");
  const cellFor = (c, v) => {
    const cs = st(c);
    if (v == null || v === "") return cs.nullDisplay !== undefined && cs.nullDisplay !== "" ? cs.nullDisplay : emptyTok;
    if (cs.dateFormat) { const d = fmtDatePattern(v, cs.dateFormat); if (d) return d; }
    // Item 20: ISO date strings without a column override follow the regional date format
    if (!measMeta[c] && typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v) && regionalDateFmt && regionalDateFmt !== "yyyy-MM-dd") {
      const d = formatDateRegional(v, regionalDateFmt, regionalTz); if (d) return d;
    }
    const fmtOverride = cs.format && (cs.format.type || cs.format.decimals !== undefined || cs.format.scale || cs.format.currency);
    if (fmtOverride && (typeof v === "number" || Number.isFinite(Number(v)))) {
      const base = measMeta[c]?.format || {};
      const f = { ...base, ...cs.format };
      if (f.type === "number") f.type = "";
      return formatValue(v, f, loc);
    }
    if (measMeta[c]) return formatValue(v, measMeta[c].format, loc);
    return typeof v === "number" ? formatNumber(v, loc) : v;
  };
  const alignCls = (c) => ({ center: "text-center", right: "text-right", left: "text-left" }[st(c).align || val.align] || "text-left");
  const colValues = (c) => (data.rows || []).map((r) => r[c]);
  const activeHdr = columns.filter((c) => isFilterActive(hdrFilters[c]));
  const widthStyle = (c) => {
    const cs = st(c);
    const s = {};
    if (cs.width) { s.width = `${cs.width}px`; s.minWidth = `${cs.width}px`; if (t9.wrap || cs.wrap) s.maxWidth = `${cs.width}px`; }
    if (cs.minWidth) s.minWidth = `${cs.minWidth}px`;
    if (cs.maxWidth) s.maxWidth = `${cs.maxWidth}px`;
    return Object.keys(s).length ? s : undefined;
  };
  const wrapCls = (c) => (t9.wrap || (c && st(c).wrap) ? "whitespace-normal break-words" : "whitespace-nowrap");
  const padCls = t9.dense ? "px-2 py-1" : "px-3 py-1.5";
  const outline = ev?.widgetOutline();

  // ---- item 24 style layers ----
  const headerStyle = {
    ...fontCss(hdr),
    ...(hdr.bg ? { backgroundColor: hdr.bg } : {}),
    ...(hdr.height ? { height: `${hdr.height}px` } : {}),
    ...(hdr.padding !== undefined && hdr.padding !== "" ? { padding: `${hdr.padding}px` } : {}),
    ...(borderCss(hdr.border) ? { border: borderCss(hdr.border) } : {}),
    ...(hdr.opacity !== undefined && hdr.opacity < 1 ? { opacity: hdr.opacity } : {}),
  };
  const valueBase = {
    ...fontCss(val),
    ...(val.bg ? { backgroundColor: val.bg } : {}),
    ...(val.padding !== undefined && val.padding !== "" ? { padding: `${val.padding}px` } : {}),
    ...(borderCss(val.border) ? { border: borderCss(val.border) } : {}),
  };
  delete valueBase.textAlign; // alignment handled per-column class
  const colStyle = (c) => {
    const cs = st(c);
    const s = { ...fontCss(cs) };
    delete s.textAlign;
    if (cs.bg) s.backgroundColor = cs.bg;
    if (cs.padding !== undefined && cs.padding !== "") s.padding = `${cs.padding}px`;
    return s;
  };
  const rowStyle = (i, fxRow) => {
    const s = fxRow ? { ...(condStyle(fxRow) || {}) } : {};
    if (!s.background && val.altRowColor && i % 2 === 1) s.background = val.altRowColor;
    if (val.rowHeight) s.height = `${val.rowHeight}px`;
    return Object.keys(s).length ? s : undefined;
  };
  const showHeaderRow = hdr.show !== false;

  return (
    <div data-testid="table-widget" className={`h-full w-full flex flex-col rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] overflow-hidden ${uid}`}
      style={outline ? { boxShadow: `inset 0 0 0 2px ${outline}` } : undefined}>
      {val.hoverColor && <style>{`.${uid} tbody tr:hover>td{background:${val.hoverColor} !important}`}</style>}
      {activeHdr.length > 0 && (
        <div data-testid="table-filter-chips" className="shrink-0 flex items-center gap-1 flex-wrap px-2 py-1 border-b border-[color:var(--glass-border)] bg-[color:var(--surface-2)]/60 text-[10px]">
          {activeHdr.map((c) => (
            <span key={c} data-testid={`table-filter-chip-${c}`} className="inline-flex items-center gap-1 rounded-full border border-[color:var(--brand-ocean)]/40 bg-[color:var(--surface)] px-2 py-0.5 text-[color:var(--ink-2)]">
              <b className="font-medium">{labelFor(c)}</b> {describeFilter(hdrFilters[c], measMeta[c] ? "number" : detectColumnKind(colValues(c).slice(0, 200)))}
              <button data-testid={`table-filter-chip-clear-${c}`} onClick={() => setHdrFilter(c, null)} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]" aria-label={`Clear filter on ${labelFor(c)}`}><X className="w-2.5 h-2.5" /></button>
            </span>
          ))}
          <button data-testid="table-filter-clear-all" onClick={() => { setLocalHdrFilters({}); if (onConfigChange) onConfigChange({ options: { ...(config.options || {}), tableOpts: { ...t9, colFilters: {} } } }); }} className="ml-auto text-[color:var(--ink-3)] hover:text-[color:var(--danger)]">Clear all</button>
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-auto">
      <table className={`w-max min-w-full ${t9.dense ? "text-xs" : "text-sm"}`} style={borderCss(val.border) ? { borderCollapse: "collapse" } : undefined}>
        <thead className="sticky top-0 bg-[color:var(--surface-2)] z-10">
          {showHeaderRow && (
            <tr>{columns.map((c) => (
              <th key={c} style={{ ...headerStyle, ...(widthStyle(c) || {}) }} aria-sort={sort?.col === c ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                className={`${padCls} py-2 font-medium text-[color:var(--ink-2)] ${hdr.wrap ? "whitespace-normal break-words" : "whitespace-nowrap"} ${{ center: "text-center", right: "text-right" }[hdr.align || st(c).align] || "text-left"}`}>
                <span className={`group/th inline-flex items-center gap-0.5 ${(hdr.align || st(c).align) === "right" ? "flex-row-reverse" : ""}`}>
                  <button data-testid={`table-sort-${c}`} onClick={() => cycleSort(c)} title="Sort column" aria-label={`Sort by ${labelFor(c)}${sort?.col === c ? (sort.dir === "asc" ? ", ascending" : ", descending") : ""}`}
                    className="inline-flex items-center gap-1 hover:text-[color:var(--brand-ocean)] transition-colors">
                    <span>{labelFor(c)}</span>{sortIcon(c)}
                  </button>
                  {t9.headerFilters !== false && (
                    <ColumnFilterPopover column={c} label={labelFor(c)} values={colValues(c)} filter={hdrFilters[c]} testid={`table-colfilter-${c}`}
                      kind={measMeta[c] ? "number" : detectColumnKind(colValues(c).slice(0, 200))} onApply={(ff) => setHdrFilter(c, ff)} />
                  )}
                </span>
              </th>
            ))}</tr>
          )}
          {t9.filterRow && (
            <tr className="bg-[color:var(--surface-2)]">
              {columns.map((c) => (
                <th key={c} className="px-1.5 pb-1.5 pt-0">
                  <input data-testid={`table-filter-${c}`} value={colFilters[c] || ""} onChange={(e) => setColFilters((f) => ({ ...f, [c]: e.target.value }))}
                    placeholder={isNumCol(c) ? "> 100, 5..20\u2026" : "Filter\u2026"}
                    className="w-full h-6 px-2 rounded-md border border-[color:var(--glass-border)] bg-[color:var(--surface)] text-[11px] font-normal text-[color:var(--ink-2)] placeholder:text-[color:var(--ink-3)]/60 focus:outline-none focus:ring-1 focus:ring-[color:var(--brand-ocean)]" />
                </th>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {pageRows.map((r, i) => {
            const fxRow = ev ? ev.row(r) : null;
            return (
              <tr key={`${i}-${String(r[columns[0]] ?? "")}`} style={rowStyle(i, fxRow)} data-testid="table-row"
                onContextMenu={onRowDrill && drillTrigger === "right_click" ? (e) => { e.preventDefault(); onRowDrill(r, e); } : undefined}
                onDoubleClick={onRowDrill && drillTrigger === "double_click" ? (e) => onRowDrill(r, e) : undefined}
                onClick={onRowDrill && drillTrigger === "click" ? (e) => onRowDrill(r, e) : undefined}
                className={"border-t border-[color:var(--glass-border)] " + (onRowDrill ? "cursor-pointer " : "") + (val.hoverColor ? "" : "hover:bg-[color:var(--surface-2)]/60 ") + (t9.striped && !val.altRowColor && i % 2 === 1 ? "bg-[color:var(--surface-2)]/35" : "")}>
                {columns.map((c) => {
                  const fx = ev ? ev.cell(r, c) : null;
                  const iconLeft = fx?.icon && fx.iconPos !== "right" && <CondIcon fx={fx} />;
                  const iconRight = fx?.icon && fx.iconPos === "right" && <CondIcon fx={fx} />;
                  const cs = st(c);
                  return (
                    <td key={c} style={{ ...valueBase, ...colStyle(c), ...(condStyle(fx) || {}), ...(widthStyle(c) || {}), ...(cs.valign || val.valign ? { verticalAlign: cs.valign || val.valign } : {}) }}
                      className={`${padCls} text-[color:var(--ink-2)] ${wrapCls(c)} ${alignCls(c)}`}>
                      {iconLeft}{fx?.iconPos === "only" ? null : cellFor(c, r[c])}{iconRight}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {!rows.length && (
            <tr><td colSpan={columns.length || 1} className="px-3 py-6 text-center text-xs text-[color:var(--ink-3)]">{t("no_rows_match")}</td></tr>
          )}
        </tbody>
      </table>
      </div>
      {pageSize > 0 && rows.length > pageSize && (
        <div data-testid="table-pagination" className="shrink-0 flex items-center justify-between gap-2 px-2.5 py-1 border-t border-[color:var(--glass-border)] bg-[color:var(--surface-2)]/70 text-[11px] text-[color:var(--ink-3)]">
          <span data-testid="table-page-info">
            {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, rows.length)} {t("of")} {rows.length} {t("rows")}
          </span>
          <span className="flex items-center gap-0.5">
            <button data-testid="table-page-prev" aria-label="Previous page" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="p-0.5 rounded hover:bg-[color:var(--surface)] disabled:opacity-30 disabled:cursor-default"><ChevronLeft className="w-3.5 h-3.5" /></button>
            <span className="px-1 text-[color:var(--ink-2)] font-medium">{safePage + 1} / {totalPages}</span>
            <button data-testid="table-page-next" aria-label="Next page" disabled={safePage >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              className="p-0.5 rounded hover:bg-[color:var(--surface)] disabled:opacity-30 disabled:cursor-default"><ChevronRight className="w-3.5 h-3.5" /></button>
          </span>
        </div>
      )}
    </div>
  );
}
